/**
 * CitiesPage — öffentliche Städte-Übersicht (Städteseiten-Relaunch 09.2026)
 *
 * v1 hatte hier (nginx-Logs vor dem 04.09.2026-Relaunch) 594 Aufrufe auf
 * /cities, 128.733 auf /cities/:id — die Städteseiten waren Landeseiten aus
 * der Suche. Route liegt bewusst AUSSERHALB von `_auth` (siehe
 * routes/cities.tsx), gleiches Muster wie /hotspots/:id.
 */
import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useCities } from './useCities'
import { fmt } from '../../lib/fmt'
import { setPublicPageMeta, publicUrl } from '../../lib/seo'
import './cities-public.css'

export function CitiesPage() {
  const { t } = useTranslation('cities')
  // total_rewards_30d_hnt ist nicht sortierbar (siehe Api::V2::CitiesQuery::ALLOWED_SORT_FIELDS
  // -- die Spalte wird live aggregiert, Sortierung darüber bräuchte einen Join über alle Städte).
  const { data, isLoading, isError } = useCities({ sort: 'hotspots_count', order: 'desc' })

  useEffect(() => {
    return setPublicPageMeta({
      title: t('list.seoTitle'),
      description: t('list.seoDescription'),
      canonical: publicUrl('/cities'),
    })
  }, [t])

  const cities = data?.data ?? []

  return (
    <div className="cities-page" data-testid="cities-page">
      <div className="section-header">
        <div>
          <h1>{t('list.title')}</h1>
          <p className="muted">{t('list.subtitle')}</p>
        </div>
      </div>

      {isLoading && (
        <div className="list" data-testid="cities-loading">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="list-row skeleton" style={{ height: 60 }} />
          ))}
        </div>
      )}

      {isError && (
        <div className="card card-pad state-error" data-testid="cities-error">
          <p>{t('list.error')}</p>
        </div>
      )}

      {!isLoading && !isError && cities.length === 0 && (
        <div className="card card-pad state-empty" data-testid="cities-empty">
          <p>{t('list.empty')}</p>
        </div>
      )}

      {!isLoading && !isError && cities.length > 0 && (
        <div className="list" role="list" aria-label={t('list.title')} data-testid="cities-list">
          {cities.map((city, i) => (
            <Link
              key={city.city_id}
              to="/cities/$id"
              params={{ id: String(city.city_id) }}
              className="list-row"
              data-testid={`city-row-${i}`}
            >
              <div className="cp-cell-identity">
                <div className="identity-title">{city.name}</div>
                <div className="identity-sub">{city.country_code}</div>
              </div>
              <div className="cp-cell-earned num-md">{fmt(city.total_rewards_30d_hnt)}</div>
              <div className="cp-cell-hotspots num-sm">
                {t('list.hotspotsCount', { count: city.hotspots_count })}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
